import ExtensionIcon from '@mui/icons-material/Extension';
import SettingsIcon from '@mui/icons-material/Settings';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import TuneIcon from '@mui/icons-material/Tune';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import MovieIcon from '@mui/icons-material/Movie';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import HomeIcon from '@mui/icons-material/Home';
import TvIcon from '@mui/icons-material/Tv';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import { useTheme } from '@mui/material/styles';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import type { Permission } from '../api/types';
import { useAuth, useDistinctServerName } from '../auth/AuthContext';
import { useDiscoveryState } from '../api/useDiscoveryState';
import { PageTitleContext } from './PageTitle';
import { barOffset, glass } from '../theme';
import AppearanceMenu from './AppearanceMenu';
import LanguageMenu from './LanguageMenu';
import SearchField from './SearchField';
import EmiroLogo from './EmiroLogo';

const SIDEBAR_WIDTH = 256;

/// The sidebar floats: inset from the window by this much, as a panel of its own rather than a
/// wall down the side.
const SIDEBAR_INSET = 12;

interface NavItem {
  path: string;
  labelKey: string;
  icon: ReactNode;
  /// Hidden from users who do not hold it. The server enforces the same rule; this only keeps
  /// the UI from offering doors that are locked.
  permission: Permission;
}

/// Two audiences, not two menus. Someone who only watches never sees the administration
/// group at all - not the libraries a film came from, not the plugin that provided it.
const builtInAreas: { key: 'User' | 'Administration'; titleKey: string; items: NavItem[] }[] = [
  {
    key: 'User',
    titleKey: 'nav.section.media',
    items: [
      { path: '/dashboard', labelKey: 'nav.dashboard', icon: <HomeIcon />, permission: 'ViewLibraries' },
      { path: '/movies', labelKey: 'nav.movies', icon: <MovieIcon />, permission: 'ViewLibraries' },
      { path: '/series', labelKey: 'nav.series', icon: <TvIcon />, permission: 'ViewLibraries' },
      { path: '/livetv', labelKey: 'nav.liveTv', icon: <LiveTvIcon />, permission: 'ViewLiveTv' },
    ],
  },
  {
    key: 'Administration',
    titleKey: 'nav.section.administration',
    items: [
      // Libraries are maintenance: scanning, paths, providers. Watching needs none of it, so
      // it takes the permission that manages them rather than the one that reads them.
      { path: '/libraries', labelKey: 'nav.libraries', icon: <VideoLibraryIcon />, permission: 'ManageLibraries' },
      // Finding something the library does not have yet. Its own permission, because it
      // reaches outside the server rather than reading what is already on it - and hidden
      // altogether where the shelf itself shows the catalogue, since browsing already is this.
      { path: '/discover', labelKey: 'nav.discover', icon: <TravelExploreIcon />, permission: 'Discover' },
      { path: '/sessions', labelKey: 'nav.sessions', icon: <PlayCircleIcon />, permission: 'ViewSessions' },
      // Where live television comes from, its channels and its bouquets, for every live TV
      // plugin at once - no plugin gets a menu entry of its own.
      {
        path: '/livetv/channels',
        labelKey: 'nav.liveTvChannels',
        icon: <SettingsInputAntennaIcon />,
        permission: 'ManageLiveTv',
      },
      { path: '/plugins', labelKey: 'nav.plugins', icon: <ExtensionIcon />, permission: 'ViewSystemStatus' },
      // Users, the server's name and transcoding all live behind one entry with a submenu.
      { path: '/settings', labelKey: 'nav.settings', icon: <SettingsIcon />, permission: 'ManageUsers' },
    ],
  },
];

/// The frame every signed-in screen lives in: a sidebar that stays put, and a slim bar
/// above the content. Deliberately close to what people already know from Plex and Emby -
/// navigation belongs on the side, and the content area belongs to the media.
export default function AppShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const permanent = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setup, signOut, can } = useAuth();
  const discovery = useDiscoveryState();
  const serverName = useDistinctServerName();

  // What the bar says, when the screen under it is not one of the menu's own. Pages announce
  // themselves through PageTitleContext; setPageTitle never changes, so announcing is cheap.
  const [pageTitle, setPageTitle] = useState<string>();

  const [drawerOpen, setDrawerOpen] = useState(false);

  // The bar is clear over the top of a page and turns to glass once the page moves under it,
  // the way the website's header does.
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 8 });
  const [userMenu, setUserMenu] = useState<HTMLElement | null>(null);

  // The longest matching path wins, so /livetv/channels lights up its own entry and not Live TV.
  const current = builtInAreas
    .flatMap((section) => section.items.map((item) => ({ section, item })))
    .filter(({ item }) => location.pathname.startsWith(item.path))
    .sort((a, b) => b.item.path.length - a.item.path.length)[0];

  const navigation = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 2.5, pt: 3, pb: 2.5 }}>
        {/* The logo carries the name, mark and wordmark side by side. Underneath it goes which
            server this is - unless that is the name the logo just gave. */}
        <Box
          component="button"
          onClick={() => {
            navigate('/dashboard');
            setDrawerOpen(false);
          }}
          aria-label={t('nav.dashboard')}
          sx={{
            appearance: 'none',
            border: 0,
            p: 0.5,
            m: -0.5,
            background: 'none',
            cursor: 'pointer',
            borderRadius: 2,
            display: 'flex',
          }}
        >
          <EmiroLogo layout="inline" size={30} glow={theme.palette.mode === 'dark'} />
        </Box>

        {serverName && (
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.25,
              py: 0.75,
              borderRadius: 999,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'action.hover',
              minWidth: 0,
            }}
          >
            {/* The server is up - this page would not be here otherwise. */}
            <Box
              aria-hidden
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: 'primary.main',
                boxShadow: (th) => `0 0 10px ${th.palette.primary.main}`,
              }}
            />
            <Typography variant="caption" noWrap sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {serverName}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ px: 1.5, flexGrow: 1, overflowY: 'auto' }}>
        {builtInAreas.map((section) => {
          const items = section.items.filter(
            (item) =>
              can(item.permission)
              // Where the shelf itself shows the catalogue there is nothing left for a separate
              // place to do, and two entries saying the same thing is a menu nobody wanted.
              && !(item.path === '/discover' && (discovery.catalogueInLibrary || discovery.playsOnDemand)),
          );

          if (items.length === 0) {
            return null;
          }

          return (
            <Box key={section.titleKey} sx={{ mb: 2 }}>
              <Typography variant="overline" sx={{ px: 1.5, color: 'text.secondary', opacity: 0.8 }}>
                {t(section.titleKey)}
              </Typography>
              <List disablePadding sx={{ mt: 0.5 }}>
                {items.map((item) => (
                  <ListItemButton
                    key={item.path}
                    selected={current?.item.path === item.path}
                    onClick={() => {
                      navigate(item.path);
                      setDrawerOpen(false);
                    }}
                    sx={{ mb: 0.25, py: 0.9, '& .MuiSvgIcon-root': { fontSize: 21 } }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText
                      slotProps={{ primary: { sx: { fontWeight: 600, fontSize: 14.5, letterSpacing: '-0.01em' } } }}
                      primary={t(item.labelKey)}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.75 }}>
          {t('app.name')} {setup?.serverVersion ?? ''}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100dvh',
        '--emiro-shell-start': permanent ? `${SIDEBAR_WIDTH + SIDEBAR_INSET}px` : '0px',
      }}
    >
      <Drawer
        variant={permanent ? 'permanent' : 'temporary'}
        open={permanent || drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: permanent ? SIDEBAR_WIDTH + SIDEBAR_INSET : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': permanent
            ? {
              width: SIDEBAR_WIDTH,
              boxSizing: 'border-box',
              m: `${SIDEBAR_INSET}px`,
              height: `calc(100% - ${SIDEBAR_INSET * 2}px)`,
              borderRadius: '24px',
              border: '1px solid',
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.09)' : 'divider',
              // Glass, so a hero that runs to the window's edge shows through it, blurred.
              ...glass(theme.palette.mode, 'thin'),
              backgroundImage: theme.palette.mode === 'dark'
                ? 'linear-gradient(180deg, rgba(19,216,210,0.06), transparent 220px),'
                  + ' linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.015))'
                : 'none',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 1px 0 rgba(255,255,255,0.06) inset, 0 30px 80px -30px rgba(0,0,0,0.9)'
                : '0 20px 50px -30px rgba(16,19,25,0.25)',
            }
            : { width: SIDEBAR_WIDTH, boxSizing: 'border-box', borderRadius: 0 },
        }}
      >
        {navigation}
      </Drawer>

      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <AppBar
          position="sticky"
          color="transparent"
          sx={{
            // A panel of its own, floating like the sidebar beside it: clear over the top of a
            // page, glass once the page moves under it.
            top: { xs: 8, md: 12 },
            mt: { xs: 1, md: 1.5 },
            mx: { xs: 1, md: 1.5 },
            width: 'auto',
            borderRadius: '20px',
            border: '1px solid transparent',
            transition: 'background-color 400ms ease, border-color 400ms ease, box-shadow 400ms ease',
            ...(scrolled && {
              ...glass(theme.palette.mode),
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.09)' : 'divider',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 1px 0 rgba(255,255,255,0.06) inset, 0 20px 50px -24px rgba(0,0,0,0.9)'
                : '0 16px 40px -26px rgba(16,19,25,0.3)',
            }),
          }}
        >
          <Toolbar
            sx={{
              gap: 1,
              minHeight: { xs: barOffset.xs - 8, md: barOffset.md - 12 },
              px: { xs: 1, md: 2.5 },
            }}
          >
            {!permanent && (
              <IconButton edge="start" onClick={() => setDrawerOpen(true)} aria-label={t('nav.menu')}>
                <MenuIcon />
              </IconButton>
            )}

            <Typography
              variant="h3"
              component="h1"
              sx={{ flexGrow: 1, fontSize: 19, fontWeight: 700, display: { xs: 'none', sm: 'block' } }}
            >
              {pageTitle ?? (current ? t(current.section.titleKey) : t('app.name'))}
            </Typography>

            <SearchField />

            <AppearanceMenu />

            <LanguageMenu />

            <IconButton onClick={(event) => setUserMenu(event.currentTarget)} aria-label={user?.name ?? ''}>
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  fontSize: 14,
                  boxShadow: theme.palette.mode === 'dark' ? '0 0 0 2px rgba(255,255,255,0.08)' : 'none',
                }}
              >
                {user?.name.slice(0, 1).toUpperCase()}
              </Avatar>
            </IconButton>

            <Menu anchorEl={userMenu} open={userMenu !== null} onClose={() => setUserMenu(null)}>
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2">{user?.name}</Typography>
                <Chip
                  size="small"
                  label={t(`account.role.${user?.role ?? 'User'}`)}
                  color={user?.role === 'SuperAdmin' ? 'primary' : 'default'}
                  sx={{ mt: 0.75 }}
                />
              </Box>
              <Divider />
              {/* Everybody has these, including the people who never see the administration
                  section. */}
              <MenuItem
                onClick={() => {
                  setUserMenu(null);
                  navigate('/preferences');
                }}
              >
                <ListItemIcon>
                  <TuneIcon fontSize="small" />
                </ListItemIcon>
                {t('preferences.title')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setUserMenu(null);
                  void signOut();
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                {t('common.signOut')}
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* No maximum width: a shelf of posters is meant to run to the edge of the screen the
            way it does on a television. Padding stays, and a page that wants the full width -
            the hero of a detail page - takes it back with a negative margin. Text that needs a
            measure sets its own. */}
        <Box
          component="main"
          sx={{ flexGrow: 1, minWidth: 0, px: { xs: 2, md: 4 }, py: { xs: 2, md: 3 } }}
        >
          <PageTitleContext.Provider value={setPageTitle}>{children}</PageTitleContext.Provider>
        </Box>
      </Box>
    </Box>
  );
}
